import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LayoutDashboard, Users, UsersRound, Trophy, Calendar, ScanLine, LogOut, Menu, X, Shield, KeyRound, FileText, ClipboardList, GitBranch, Timer, Award } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import TimerPanel from '@/components/TimerPanel';
import BracketCategoryPicker from '@/components/BracketCategoryPicker';
import RegistrationCategoryPicker from '@/components/RegistrationCategoryPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [timerOpen, setTimerOpen] = useState(false);
  const [bracketPickerOpen, setBracketPickerOpen] = useState(false);
  const [registrationPickerOpen, setRegistrationPickerOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { admin, isSuperAdmin, canViewAdmins, canViewAuditLogs, canViewCompetitionSystems } = useAuth();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin');
    navigate('/login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Password baru dan konfirmasi tidak sama');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password berhasil diubah');
      setChangePasswordOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengubah password');
    }
  };

  const mobileQuickActions = [
    { path: '/verify', label: 'Scan QR', icon: ScanLine },
    { type: 'registration', label: 'Registrasi', icon: ClipboardList },
    { type: 'bracket', label: 'Bracket', icon: GitBranch },
    { path: '/tournaments', label: 'Turnamen', icon: Trophy },
    { type: 'timer', label: 'Timer', icon: Timer },
    { path: '/players', label: 'Players', icon: Users },
  ];

  const menuItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/players', label: 'Players', icon: Users },
    { path: '/teams', label: 'Teams', icon: UsersRound },
    { path: '/tournaments', label: 'Turnamen', icon: Trophy },
    { path: '/registrations', label: 'Registrasi', icon: ClipboardList },
    { path: '/schedules', label: 'Jadwal', icon: Calendar },
    { path: '/verify', label: 'Scan QR', icon: ScanLine },
    ...(canViewAdmins() ? [{ path: '/admins', label: 'Admin', icon: Shield }] : []),
    ...(canViewCompetitionSystems() ? [{ path: '/competition-systems', label: 'Sistem Pertandingan', icon: Award }] : []),
    ...(canViewAuditLogs() ? [{ path: '/audit-logs', label: 'Audit Log', icon: FileText }] : []),
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="app-layout">
      {/* Header */}
      <header className="bg-white/70 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="flex items-center justify-between px-3 py-3 sm:px-4 sm:py-4 lg:px-6 gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <button
              data-testid="sidebar-toggle-btn"
              className="lg:hidden p-2.5 -ml-1 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-slate-100 rounded-lg touch-manipulation"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <h1 className="text-lg sm:text-2xl font-bold text-sky-600 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Sky Runner Admin
            </h1>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <button
              type="button"
              data-testid="header-timer-btn"
              onClick={() => setTimerOpen(true)}
              className="hidden lg:inline-flex items-center gap-2 px-3 py-2.5 min-h-[44px] rounded-xl text-slate-600 hover:bg-slate-100 font-medium text-sm transition-colors touch-manipulation"
              title="Timer countdown"
            >
              <Timer size={20} className="shrink-0" />
              <span>Timer</span>
            </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-btn"
                className="flex items-center gap-2 px-3 py-2.5 min-h-[44px] min-w-[44px] justify-center sm:justify-end text-slate-600 hover:bg-slate-100 rounded-full transition-colors touch-manipulation"
              >
                <span className="hidden sm:inline font-medium">{admin?.nama || 'Admin'}</span>
                <span className="hidden sm:inline text-xs text-slate-500">({admin?.role || 'admin'})</span>
                <LogOut size={18} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => setChangePasswordOpen(true)}>
                <KeyRound size={16} className="mr-2" />
                Ubah Password
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="text-red-600">
                <LogOut size={16} className="mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>

          <Dialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Ubah Password</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleChangePassword} className="space-y-4 pt-4">
                <div>
                  <label className="text-sm font-medium">Password saat ini</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Password baru</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Konfirmasi password baru</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
                <div className="flex gap-2 pt-4">
                  <Button type="submit">Simpan</Button>
                  <Button type="button" variant="outline" onClick={() => setChangePasswordOpen(false)}>
                    Batal
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 transition-transform duration-300`}
          style={{ top: '73px' }}
        >
          <nav className="p-3 sm:p-4 space-y-1 sm:space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 min-h-[48px] rounded-xl font-medium transition-all touch-manipulation active:bg-slate-100 ${
                    isActive
                      ? 'bg-sky-50 text-sky-600 shadow-sm'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Icon size={20} className="flex-shrink-0" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            <button
              type="button"
              data-testid="sidebar-timer-btn"
              onClick={() => {
                setTimerOpen(true);
                setSidebarOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 min-h-[48px] rounded-xl font-medium transition-all touch-manipulation text-slate-600 hover:bg-slate-50"
            >
              <Timer size={20} className="flex-shrink-0" />
              <span>Timer</span>
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-3 sm:p-4 lg:p-8 overflow-x-hidden pb-20 lg:pb-0">
          <div className="max-w-7xl mx-auto animate-slide-in w-full min-w-0">{children}</div>
        </main>
      </div>

      {/* Mobile quick actions footer - only on mobile */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 safe-area-pb">
        <div className="flex items-center justify-around px-1 py-2 max-w-screen-xl mx-auto">
          {mobileQuickActions.map((item) => {
            const Icon = item.icon;
            const isTimer = item.type === 'timer';
            const isBracket = item.type === 'bracket';
            const isRegistration = item.type === 'registration';
            const isActive = !isTimer && !isBracket && !isRegistration && location.pathname === item.path;
            if (isRegistration) {
              return (
                <button
                  key="registration"
                  type="button"
                  onClick={() => setRegistrationPickerOpen(true)}
                  className="flex flex-col items-center justify-center gap-0.5 min-w-[56px] min-h-[56px] rounded-xl touch-manipulation active:scale-95 transition-transform text-slate-600 hover:bg-slate-50"
                  title={item.label}
                >
                  <Icon size={22} className="shrink-0" strokeWidth={2} />
                  <span className="text-[10px] font-medium leading-tight max-w-[64px] truncate">{item.label}</span>
                </button>
              );
            }
            if (isBracket) {
              return (
                <button
                  key="bracket"
                  type="button"
                  onClick={() => setBracketPickerOpen(true)}
                  className="flex flex-col items-center justify-center gap-0.5 min-w-[56px] min-h-[56px] rounded-xl touch-manipulation active:scale-95 transition-transform text-slate-600 hover:bg-slate-50"
                  title={item.label}
                >
                  <Icon size={22} className="shrink-0" strokeWidth={2} />
                  <span className="text-[10px] font-medium leading-tight max-w-[64px] truncate">{item.label}</span>
                </button>
              );
            }
            if (isTimer) {
              return (
                <button
                  key="timer"
                  type="button"
                  onClick={() => setTimerOpen(true)}
                  className="flex flex-col items-center justify-center gap-0.5 min-w-[56px] min-h-[56px] rounded-xl touch-manipulation active:scale-95 transition-transform text-slate-600 hover:bg-slate-50"
                  title={item.label}
                >
                  <Icon size={22} className="shrink-0" strokeWidth={2} />
                  <span className="text-[10px] font-medium leading-tight max-w-[64px] truncate">{item.label}</span>
                </button>
              );
            }
            return (
              <Link
                key={`${item.path}-${item.label}`}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex flex-col items-center justify-center gap-0.5 min-w-[56px] min-h-[56px] rounded-xl touch-manipulation active:scale-95 transition-transform ${
                  isActive ? 'text-sky-600 bg-sky-50' : 'text-slate-600 hover:bg-slate-50'
                }`}
                title={item.label}
              >
                <Icon size={22} className="shrink-0" strokeWidth={isActive ? 2.5 : 2} />
                <span className="text-[10px] font-medium leading-tight max-w-[64px] truncate">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/20 z-30"
          style={{ top: '73px' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <TimerPanel open={timerOpen} onOpenChange={setTimerOpen} />
      <BracketCategoryPicker
        open={bracketPickerOpen}
        onOpenChange={setBracketPickerOpen}
        onSelect={(path) => {
          navigate(path);
          setSidebarOpen(false);
        }}
      />
      <RegistrationCategoryPicker
        open={registrationPickerOpen}
        onOpenChange={setRegistrationPickerOpen}
        onSelect={(path) => {
          navigate(path);
          setSidebarOpen(false);
        }}
      />
    </div>
  );
};

export default Layout;